import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NdisNumberValidator } from '../validators/ndis-number.validator';
import { SAFormValidator } from '../validators/sa-form.validator';
import { dateToString } from '../validators/date.validator';
import { Meta, Title } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { ServiceAgreement } from '../models/service-agreement.model';
import { ServiceAgreementService } from '../services/service-agreement.service';
import { SnackService } from '../services/snack.service';
import { LettersOnlyValidator } from '../validators/letters-only.validator';
import { PhoneNumberValidator } from '../validators/phone-number.validator';
import { AlphaNumericValidator } from '../validators/alpha-numeric.validator';
import { PostCodeValidator } from '../validators/postcode.validator';
import { CalendarHeaderComponent } from '../calendar-header/calendar-header.component';
import { NgxGpAutocompleteDirective } from '@angular-magic/ngx-gp-autocomplete';
// import { Address } from 'ngx-google-places-autocomplete/objects/address';
// import { Options } from 'ngx-google-places-autocomplete/objects/options/options';

@Component({
  selector: 'service-agreement-page',
  templateUrl: './service-agreement-page.component.html',
  styleUrls: ['./service-agreement-page.component.scss']
})
export class ServiceAgreementPageComponent implements OnInit {
  @ViewChild('participantStreet') participantStreet:
    | NgxGpAutocompleteDirective
    | undefined;
  @ViewChild('nomineeStreet') nomineeStreet:
    | NgxGpAutocompleteDirective
    | undefined;
  @ViewChild('supportCoStreet') supportCoStreet:
    | NgxGpAutocompleteDirective
    | undefined;
  calendarHeader = CalendarHeaderComponent;
  ndisNumberExists?: boolean;
  serviceAgreement!: FormGroup;
  formUpdate?: boolean;
  submitted = false;
  delivered = false;
  signer?: string;
  relationship?: boolean = false;
  email?: string;

  // This is a shorter form reference for the HTML Template
  get f(): any {
    return this.serviceAgreement.controls;
  }

  // This SEO data object can be replaced by a call to the server when ready.
  seoData = {
    title: 'ADIEM | NDIS Service Agreement',
    description:
      'An ADIEM Plan Management Service Agreement to allow us to help you manage your NDIS budget. This form will pre-populate \
       a service agreement that will be sent to you via email. Please contact us for more information.',
    image:
      'https://firebasestorage.googleapis.com/v0/b/adiem-prod.appspot.com/o/images%2Fplan-manager-contact-details.jpg?alt=media&token=e41695b6-4ead-49b9-916a-db16233ece18'
  };

  // These are the options for the Google Maps Places Autocomplete
  addressOptions = {
    types: [],
    componentRestrictions: { country: 'AU' },
    bounds: undefined,
    fields: [],
    strictBounds: false,
    origin: undefined
  };

  constructor(
    private fb: FormBuilder,
    private ndisValidator: NdisNumberValidator,
    private lettersOnlyValidator: LettersOnlyValidator,
    private phoneNumberValidator: PhoneNumberValidator,
    private alphaNumericValidator: AlphaNumericValidator,
    private postcodeValidator: PostCodeValidator,
    private sAFormValidator: SAFormValidator,
    private serviceAgreementService: ServiceAgreementService,
    private snackService: SnackService,
    private title: Title,
    private meta: Meta,
    public datepipe: DatePipe
  ) {}

  ngOnInit() {
    this.title.setTitle(this.seoData.title);
    this.meta.addTags([
      { name: 'twitter:card', content: 'summary' },
      { name: 'og:url', content: '/contact' },
      { name: 'og:title', content: this.seoData.title },
      { name: 'og:description', content: this.seoData.description },
      { name: 'og:image', content: this.seoData.image }
    ]);

    this.serviceAgreement = this.fb.group(
      {
        // Plan Details
        ndisNumber: ['', this.ndisValidator.ndisNumberValidator],
        planStartDate: [''],
        planEndDate: [''],

        // Participant Details
        participantFirstName: [
          '',
          [Validators.required, this.lettersOnlyValidator.lettersOnlyValidator]
        ],
        participantLastName: [
          '',
          [Validators.required, this.lettersOnlyValidator.lettersOnlyValidator]
        ],
        participantDOB: ['', Validators.required],
        participantPhone: [
          null,
          this.phoneNumberValidator.phoneNumberValidator
        ],
        participantEmail: [
          null,
          [
            Validators.email,
            Validators.pattern(
              '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
            )
          ]
        ],
        participantStreet: [
          null,
          this.alphaNumericValidator.alphaNumericValidator
        ],
        participantCity: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        participantPostcode: [null, this.postcodeValidator.postcodeValidator],
        participantState: [
          null,
          this.lettersOnlyValidator.lettersOnlyValidator
        ],
        participantStatements: [false],

        // Nominee Details
        nomineeFirstName: [
          null,
          this.lettersOnlyValidator.lettersOnlyValidator
        ],
        nomineeLastName: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        nomineePhone: [null, this.phoneNumberValidator.phoneNumberValidator],
        nomineeEmail: [
          null,
          [
            Validators.email,
            Validators.pattern(
              '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
            )
          ]
        ],
        nomineeStreet: [null, this.alphaNumericValidator.alphaNumericValidator],
        nomineeCity: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        nomineePostcode: [null, this.postcodeValidator.postcodeValidator],
        nomineeState: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        nomineeStatements: [false],

        // Support Coordinator's Details
        supportCoFirstName: [
          null,
          this.lettersOnlyValidator.lettersOnlyValidator
        ],
        supportCoLastName: [
          null,
          this.lettersOnlyValidator.lettersOnlyValidator
        ],
        supportCoPhone: [null, this.phoneNumberValidator.phoneNumberValidator],
        supportCoEmail: [
          null,
          [
            Validators.email,
            Validators.pattern(
              '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
            )
          ]
        ],
        supportCoStreet: [
          null,
          this.alphaNumericValidator.alphaNumericValidator
        ],
        supportCoCity: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        supportCoPostcode: [null, this.postcodeValidator.postcodeValidator],
        supportCoState: [null, this.lettersOnlyValidator.lettersOnlyValidator],
        supportCoStatements: [false],

        // Service Agreement Delivery Details
        servAgreeSignatory: [
          '',
          [Validators.required, this.lettersOnlyValidator.lettersOnlyValidator]
        ],
        servAgreeRelationship: [
          null,
          this.lettersOnlyValidator.lettersOnlyValidator
        ],
        servAgreeEmail: [
          '',
          [
            Validators.required,
            Validators.email,
            Validators.pattern(
              '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
            )
          ]
        ]
      },
      {
        validator: this.sAFormValidator.SAFValidator.bind(this.sAFormValidator)
      }
    );
  }

  getAddressFields(address: any) {
    const fieldValues = address.address_components;

    // Return an object of { field: value }
    const result: any = {
      subpremise: '',
      street_number: '',
      route: '',
      locality: '',
      postal_code: '',
      administrative_area_level_1: ''
    };
    for (const fieldValue of fieldValues) {
      result[fieldValue.types[0]] = fieldValue.long_name;
    }
    return result;
  }

  fillParticipantAddressFields(address: any) {
    const fields = this.getAddressFields(address);
    let unitNumber = '';
    if (this.f.participantStreet.value.match('/')) {
      unitNumber =
        this.serviceAgreement?.controls['participantStreet'].value.split(
          '/'
        )[0] + '/';
    }
    this.f.participantStreet.setValue(
      `${unitNumber}${fields.street_number} ${fields.route}`
    );
    this.f.participantCity.setValue(`${fields.locality}`);
    this.f.participantPostcode.setValue(`${fields.postal_code}`);
    this.f.participantState.setValue(`${fields.administrative_area_level_1}`);
  }

  fillNomineeAddressFields(address: any) {
    const fields = this.getAddressFields(address);
    let unitNumber = '';
    if (this.f.participantStreet.value.match('/')) {
      unitNumber =
        this.serviceAgreement?.controls['nomineeStreet'].value.split('/')[0] +
        '/';
    }
    this.f.nomineeStreet.setValue(
      `${unitNumber}${fields.street_number} ${fields.route}`
    );
    this.f.nomineeCity.setValue(`${fields.locality}`);
    this.f.nomineePostcode.setValue(`${fields.postal_code}`);
    this.f.nomineeState.setValue(`${fields.administrative_area_level_1}`);
  }

  fillSupportCoAddressFields(address: any) {
    const fields = this.getAddressFields(address);
    let unitNumber = '';
    if (this.f.participantStreet.value.match('/')) {
      unitNumber =
        this.serviceAgreement?.controls['supportCoStreet'].value.split('/')[0] +
        '/';
    }
    this.f.supportCoStreet.setValue(
      `${unitNumber}${fields.street_number} ${fields.route}`
    );
    this.f.supportCoCity.setValue(`${fields.locality}`);
    this.f.supportCoPostcode.setValue(`${fields.postal_code}`);
    this.f.supportCoState.setValue(`${fields.administrative_area_level_1}`);
  }

  // This functions takes the string 'date' value of the Moment Object
  changeMomentObjectsToStrings() {
    this.f.planStartDate.setValue(dateToString(this.f.planStartDate.value));
    this.f.planEndDate.setValue(dateToString(this.f.planEndDate.value));
    this.f.participantDOB.setValue(dateToString(this.f.participantDOB.value));
  }

  updateSADelivery() {
    const partFirstName = this.f.participantFirstName.value;
    const partLastName = this.f.participantLastName.value;
    const partEmail = this.f.participantEmail.value;
    const nomFirstName = this.f.nomineeFirstName.value;
    const nomLastName = this.f.nomineeLastName.value;
    const nomEmail = this.f.nomineeEmail.value;
    // const scFirstName = this.f.supportCoFirstName.value;
    // const scLastName = this.f.supportCoLastName.value;
    // const scEmail = this.f.supportCoEmail.value;

    // Use Participant values IF participant provides details
    if (partFirstName && partLastName && partEmail) {
      this.signer = partFirstName + ' ' + partLastName;
      this.relationship = false; // Removes relationship field
      this.email = partEmail;
    }

    // Use Nominee values IF Nominee provides details
    if (nomFirstName && nomLastName && nomEmail) {
      this.signer = nomFirstName + ' ' + nomLastName;
      this.relationship = true; // Creates relationship field
      this.f.servAgreeRelationship.touched = true;
      this.email = nomEmail;
    }

    // Use Support Coordinator values IF Support Coordinator provides details
    // if (scFirstName && scLastName && scEmail) {
    //   this.signer = scFirstName + ' ' + scLastName;
    //   this.relationship = true; // Creates relationship field
    //   this.f.servAgreeRelationship.touched = true;
    //   this.email = scEmail;
    // }

    if (this.signer) {
      this.f.servAgreeSignatory.setValue(this.signer);
      this.f.servAgreeEmail.setValue(this.email);
    }
  }

  submitForm() {
    this.findInvalidControls();

    if (this.serviceAgreement.valid) {
      const preparedData = this.prepareFormData();
      this.sendServiceAgreement(preparedData);
    }
  }

  findInvalidControls() {
    const invalid = [];
    const controls: any = this.serviceAgreement.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(`${name}: ${controls[name].value}`);
        controls[name].markAsTouched();
      }
    }
    if (invalid.length === 0) {
      // console.log('Submitted Successfully');
    } else {
      console.log('Form has Invalid Fields: ', invalid);
    }
    return invalid;
  }

  prepareFormData() {
    const formData = this.serviceAgreement.value;

    const preparedData: ServiceAgreement = {
      // Plan Details
      ndisNumber: formData.ndisNumber,
      planStartDate: formData.planStartDate,
      planEndDate: formData.planEndDate,

      // Participant Details
      participantFirstName: formData.participantFirstName,
      participantLastName: formData.participantLastName,
      participantDOB: formData.participantDOB,
      participantPhone: this.formatPhone(formData.participantPhone),
      participantEmail: formData.participantEmail,
      participantStreet: formData.participantStreet,
      participantCity: formData.participantCity,
      participantPostcode: formData.participantPostcode,
      participantState: formData.participantState,
      participantStatements: formData.participantStatements,

      // Nominee Details
      nomineeFirstName: formData.nomineeFirstName,
      nomineeLastName: formData.nomineeLastName,
      nomineePhone: this.formatPhone(formData.nomineePhone),
      nomineeEmail: formData.nomineeEmail,
      nomineeStreet: formData.nomineeStreet,
      nomineeCity: formData.nomineeCity,
      nomineePostcode: formData.nomineePostcode,
      nomineeState: formData.nomineeState,
      nomineeStatements: formData.nomineeStatements,

      // Support Coordinator's Details
      supportCoFirstName: formData.supportCoFirstName,
      supportCoLastName: formData.supportCoLastName,
      supportCoPhone: this.formatPhone(formData.supportCoPhone),
      supportCoEmail: formData.supportCoEmail,
      supportCoStreet: formData.supportCoStreet,
      supportCoCity: formData.supportCoCity,
      supportCoPostcode: formData.supportCoPostcode,
      supportCoState: formData.supportCoState,
      supportCoStatements: formData.supportCoStatements,
      // Delivery Email Address
      servAgreeSignatory: formData.servAgreeSignatory,
      servAgreeRelationship: formData.servAgreeRelationship,
      servAgreeEmail: formData.servAgreeEmail
    };

    return preparedData;
  }

  formatPhone(phone: string) {
    if (!phone) return null;
    // Remove all non-numbers
    let phone1 = phone.replace(/\D/g, '');
    let phone2 = '';
    // Remove leading 61 country code
    if (phone1.substring(0, 2) === '61') {
      phone2 = phone1.replace(/^.{2}/g, '0');
    } else {
      phone2 = phone1;
    }
    // Kee last 10 numbers
    let phone3 = phone2.slice(-10);

    return phone3;
  }

  sendServiceAgreement(serviceAgreement: ServiceAgreement) {
    this.submitted = true;
    this.delivered = false;

    this.serviceAgreementService
      .submitServiceAgreement(serviceAgreement)
      .subscribe({
        next: (response) => {
          this.snackService.passMessage(
            'Service agreement sent',
            2000,
            `service-agreement-sent`
          );
          this.delivered = true;
        },
        error: (err) => {
          // console.log('Error: ', err);
        },
        complete: () => {}
      });
  }
}
